import { FC, useMemo } from 'react'
import { QueryStatus } from '@tanstack/react-query'

import { copyToClipboard } from '@revolut-internal/ui-kit'

import { LimitedTable } from 'components/LimitedTable/LimitedTable'
import { useChannel } from 'hooks/useChannel'
import { useTableState } from 'hooks/useTableState'
import { useToasts } from 'modules/toasts/ToastsProvider'
import { UrlStateStorageKeys } from 'modules/url-state-storage/consts'
import {
  IncidentTimelineSearchRequest,
  QueueHealthMetricsOverview,
  ResourcesSideParams,
  SpecialisationHealth,
} from 'types'
import { deriveLoadingStateFromQueryStatus } from 'utils/deriveLoadingStateFromQueryStatus'

import { getColumns } from './utils/getColumns'
import { getSpecialisationReport } from './utils/getSpecialisationReport'
import { prepareTableData } from './utils/prepareTableData'

type QueuesHealthDetailsTableProps = {
  data: SpecialisationHealth[]
  queryStatus: QueryStatus
  metrics?: QueueHealthMetricsOverview
  resourcesSideParams?: ResourcesSideParams
  selectedIncidentEntity?: IncidentTimelineSearchRequest
  onShowResources: (value: ResourcesSideParams | undefined) => void
}

export const QueuesHealthDetailsTable: FC<QueuesHealthDetailsTableProps> = ({
  data,
  metrics,
  queryStatus,
  resourcesSideParams,
  onShowResources,
}) => {
  const { entityName, isDante } = useChannel()
  const { addSuccessToast } = useToasts()

  const { state: tableState, setSorting } = useTableState(
    UrlStateStorageKeys.QueueHealthDetailsTableState,
  )

  const specialisationLabel = isDante ? 'Ownership & Queues' : 'Specialisation'
  const preparedData = useMemo(() => prepareTableData(data), [data])

  const columns = useMemo(
    () =>
      getColumns({
        resourcesSideParams,
        entityName,
        specialisationLabel,
        metrics,
        isDante,
        onShowResources,
        onCopyReport: async row => {
          await copyToClipboard(getSpecialisationReport(row, entityName, isDante))

          addSuccessToast(`Report for ${row.name} copied`)
        },
      }),
    [
      resourcesSideParams,
      entityName,
      isDante,
      specialisationLabel,
      metrics,
      addSuccessToast,
      onShowResources,
    ],
  )

  return (
    <LimitedTable
      state={tableState}
      columns={columns}
      data={preparedData}
      getRowId={row => row.id}
      rowsToShow={12}
      aria-label="Queue health table"
      primaryColumnId="name"
      loadingState={deriveLoadingStateFromQueryStatus(queryStatus)}
      expandable
      resizable
      showingCount
      enableMultiSort
      onSortingChange={setSorting}
    />
  )
}
